import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/system";
import {
  Button,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { getMe } from "../../../redux/slices/userSlice";
import AlertDialogComponent from "../../../components/AlertDialogComponent";
import {
  deleteStore,
  getAllStores,
} from "../../../redux/slices/adminStoreSlice";
import AddEditStoreModal from "./AddEditStoreModal";
import { getAllProducts } from "../../../redux/slices/adminProductsSlice";

const StoreSettingTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showAddNewStoreModal, setShowAddNewStoreModal] = useState(false);
  const [selectedStoreToDelete, setSelectedStoreToDelete] = useState();
  const [selectedStoreToEdit, setSelectedStoreToEdit] = useState();
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);

  const columns = [
    {
      id: "name",
      label: t("adminSettingStore.name"),
      minWidth: 100,
    },
    {
      id: "pib",
      label: t("adminSettingStore.pib"),
      minWidth: 70,
    },
    {
      id: "product_dictionaries",
      label: t("adminSettingStore.productDictionary"),
      minWidth: 170,
    },
    {
      id: "action",
      label: t("adminSettingStore.actions"),
      minWidth: 170,
    },
  ];
  useEffect(() => {
    dispatch(getAllStores());
    dispatch(getAllProducts());
  }, [dispatch]);

  const allDefinedStores = useSelector(
    (state) => state.adminStores.allDefinedStores
  );
  const getAllStoresLoading = useSelector(
    (state) => state.adminStores.getAllStoresLoading
  );
  const allDefinedProducts = useSelector(
    (state) => state.adminProducts.allDefinedProducts
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onEditStore = (product) => {
    setSelectedStoreToEdit(product);
    setShowAddNewStoreModal(true);
  };
  const onDeleteStore = (product) => {
    setSelectedStoreToDelete(product);
    setShowDeleteWarningModal(true);
  };
  const onShowAddStoreModal = () => {
    setShowAddNewStoreModal(true);
  };
  const handleOnCloseAddNewStoreModal = () => {
    setSelectedStoreToEdit(null);
    setShowAddNewStoreModal(false);
  };
  const handleSuccessAddNewStore = () => {
    setShowAddNewStoreModal(false);
    dispatch(getMe()).then(() => dispatch(getAllStores()));
  };
  const handleSuccessEditStore = () => {
    setShowAddNewStoreModal(false);
    setSelectedStoreToEdit(null);
    dispatch(getMe()).then(() => dispatch(getAllStores()));
  };
  const onConfirmDeleteStore = async () => {
    await dispatch(getMe()).then(() => {
      dispatch(deleteStore({ objectId: selectedStoreToDelete.objectId })).then(
        () => {
          dispatch(getAllStores());
          setShowDeleteWarningModal(false);
        }
      );
    });
  };
  const handleOnCloseWarningModal = () => {
    setShowDeleteWarningModal(false);
  };
  const getProductAliases = (value) => {
    const products = [];
    value?.forEach((v) => {
      const fullProduct = allDefinedProducts.find(
        (dProduct) => dProduct.objectId === v.objectId
      );
      let i = products.find((p) => p?.objectId === fullProduct?.objectId);

      !i &&
        fullProduct &&
        products.push({
          objectId: fullProduct.objectId,
          name: fullProduct.name,
          aliases: value
            .filter((_) => _.objectId === fullProduct.objectId)
            .map((_) => _.term),
        });
    });

    return products?.map((product) => {
      return (
        <Tooltip
          color="primary"
          title={
            <Typography variant="body1">
              {product.aliases.toString()}
            </Typography>
          }
          arrow
        >
          <Chip
            key={product.objectId}
            color="error"
            sx={{ marginBottom: "6px" }}
            label={product.name}
          />
        </Tooltip>
      );
    });
  };

  const getColumn = (value, column) => {
    const isActionColumn = column.id === "action";
    let content = null;

    if (isActionColumn) {
      content = (
        <TableCell key={column.id} align={column.align}>
          <Stack direction="row">
            <Button variant="contained" onClick={() => onEditStore(value)}>
              {t("adminSettingStore.edit")}
            </Button>
            <Button
              sx={{ marginLeft: "8px" }}
              onClick={() => onDeleteStore(value)}
              color="error"
            >
              {t("adminSettingStore.delete")}
            </Button>
          </Stack>
        </TableCell>
      );
    } else if (column.id === "product_dictionaries") {
      content = (
        <TableCell key={column.id} align={column.align}>
          <Stack direction="column">{getProductAliases(value)}</Stack>
        </TableCell>
      );
    } else {
      content = (
        <TableCell key={column.id} align={column.align}>
          {value}
        </TableCell>
      );
    }

    return (
      <TableCell key={column.id} align={column.align}>
        {content}
      </TableCell>
    );
  };
  return (
    <>
      <Stack display="flex" direction="row" justifyContent="end">
        <Button variant="contained" size="small" onClick={onShowAddStoreModal}>
          {t("adminSettingStore.createStore")}
        </Button>
      </Stack>
      <Paper sx={{ width: "100%", overflow: "hidden", marginTop: "12px" }}>
        <TableContainer sx={{ marginTop: "12px" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {getAllStoresLoading === "PENDING" ? (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            ) : (
              <TableBody>
                {allDefinedStores
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((store) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={store.objectId}
                      >
                        {columns.map((column) => {
                          const value =
                            column.id === "action" ? store : store[column.id];
                          return getColumn(value, column);
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={allDefinedStores?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {showAddNewStoreModal && (
          <AddEditStoreModal
            open={showAddNewStoreModal}
            onClose={handleOnCloseAddNewStoreModal}
            storeToEdit={selectedStoreToEdit}
            isEditMode={selectedStoreToEdit}
            onSuccessAddNewStore={handleSuccessAddNewStore}
            onSuccessEditStore={handleSuccessEditStore}
            allDefinedProducts={allDefinedProducts}
            allDefinedStores={allDefinedStores}
          />
        )}
        {showDeleteWarningModal && (
          <AlertDialogComponent
            open={setShowDeleteWarningModal}
            onClose={handleOnCloseWarningModal}
            onConfirm={onConfirmDeleteStore}
            title={t("adminSettingStore.deleteStoreWarning")}
          />
        )}
      </Paper>
    </>
  );
};
export default StoreSettingTable;
